import { useRef } from "react"

import { Flex } from "@/atoms"
import { TEASERS } from "@/const/teaser.constants"
import { getDateInFormat, MONTH_YEAR } from "@/helpers/date.helpers"
import { capitalizeFirstLetter } from "@/helpers/string.helpers"
import { useTranslation } from "@/hooks/useTranslation"
import { Heading } from "@/molecules/Heading"
import { LegendChart } from "@/molecules/LegendChart"
import { discoveredClosedIssuesSelector } from "@/pages/DashboardPage/applications/useDiscoveredClosedIssues.selector"
import {
  getInsightDefaultFilters,
  useStatisticsDashboard
} from "@/pages/DashboardPage/applications/useStatistics"
import { EmptyWidget } from "@/pages/DashboardPage/components/molecules/EmptyWidget"
import { FilterTitleCard } from "@/pages/DashboardPage/components/molecules/FilterTitleCard"
import CardDashboardTemplate from "@/pages/DashboardPage/components/templates/CardDashboardTemplate"
import { DiscoveredClosedIssuesChart } from "./DiscoveredClosedIssuesChart"

import type { DiscoveredClosedIssuesResponse } from "@/api/useStatistics.types"

export const DiscoveredClosedIssuesWidget = () => {
  const { t } = useTranslation()
  const { t: tWidget } = useTranslation("translation", {
    keyPrefix: "dashboard.discoveredVsClosedIssuesWidget"
  })

  const defaultFilter = {
    critical: true,
    high: true,
    ...getInsightDefaultFilters()
  }
  const [{ response, isLoading }, state, actions] = useStatisticsDashboard<
    DiscoveredClosedIssuesResponse,
    ReturnType<typeof discoveredClosedIssuesSelector>
  >(
    { needle: "/widgets/issues-over-time", defaultFilter },
    { select: discoveredClosedIssuesSelector }
  )

  const {
    buckets = [],
    aggregatedData,
    totalFilteredIssues,
    totalIssuesInScope
  } = response || {}

  const colors = useRef(getComputedStyle(document.documentElement)).current
  const legend = [
    {
      label: capitalizeFirstLetter(t("discovered")),
      value: aggregatedData?.totalDiscovered || 0,
      color: colors.getPropertyValue("--volcano-3-color"),
      order: 1
    },
    {
      label: capitalizeFirstLetter(t("resolved")),
      value: aggregatedData?.totalResolved || 0,
      color: colors.getPropertyValue("--polar-green-3-color"),
      order: 2
    }
  ]
  const { period = "day", timePeriod = 0 } = aggregatedData || {}

  return (
    <CardDashboardTemplate
      noOverflow
      capitalize={false}
      title={tWidget("title")}
      subTitle={t("dashboard.discoveredSince", {
        date: state[0].since ? getDateInFormat(state[0].since, MONTH_YEAR) : ""
      })}
      contentPopover={tWidget("tooltip")}
      aside={
        <FilterTitleCard
          severity
          loading={isLoading}
          count={totalFilteredIssues}
          total={totalIssuesInScope}
          values={state[0]}
          onOk={state[1]}
        />
      }
      loading={isLoading}
      teasers={{
        dataReadiness: true,
        scmInstalled: TEASERS.SCM_INSTALLED.codeRepoNotConnected(t)
      }}
    >
      <Flex vertical gap={24} flex={1} className="m-w-0-ks">
        <Flex flex={1} className="m-w-0-ks">
          <EmptyWidget
            actions={actions}
            count={totalFilteredIssues}
            total={totalIssuesInScope}
          >
            <Flex className="m-w-0-ks w-100-ks" gap={70}>
              <Flex vertical justify="center" gap={8}>
                <Heading
                  level={6}
                  title={tWidget("past", {
                    period: `${timePeriod} ${t(`general.${period}`, { count: timePeriod })}`
                  })}
                />
                <LegendChart vertical items={legend} />
              </Flex>
              <Flex flex={1} className="m-w-0-ks">
                <DiscoveredClosedIssuesChart
                  legend={legend}
                  data={buckets}
                  redirectToTriage={actions.redirectToTriage}
                />
              </Flex>
            </Flex>
          </EmptyWidget>
        </Flex>
      </Flex>
    </CardDashboardTemplate>
  )
}
